import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  TableSortLabel,
  TextField,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import AdminPanelSettingsRounded from "@mui/icons-material/AdminPanelSettingsRounded";
import ClearRounded from "@mui/icons-material/ClearRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import VisibilityOffRounded from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import WelcomeHeader from "../components/WelcomeHeader";
import usuariosService from "../features/usuarios/services/usuarios.service";
import useContratosAtivos from "../hooks/useContratosAtivos";
import { getReadonlyFlag } from "../utils/getRoleToken";

const initialUserForm = {
  idUsuario: null,
  nome: "",
  email: "",
  senha: "",
  confirmacaoSenha: "",
  role: "user",
  ativo: true,
  primeiroLogin: true,
  idCorretorExterno: "",
  documentoCorretor: "",
};

const getApiErrorMessage = (err, fallbackMessage) => {
  const responseData = err?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) return responseData;
  if (responseData?.message) return responseData.message;
  if (responseData?.erro) return responseData.erro;
  if (responseData?.detail) return responseData.detail;
  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    return responseData.errors.map((item) => item?.message || item).filter(Boolean).join(" | ");
  }

  return err?.message || fallbackMessage;
};

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const getRoleChipProps = (role) => {
  if (role === "admin") {
    return { label: "Admin", chipSx: { bgcolor: "#1e3a8a", color: "#fff" } };
  }

  if (role === "corretor") {
    return { label: "Corretor", chipSx: { bgcolor: "#e5e7eb", color: "#374151" } };
  }

  if (role === "gestor") {
    return { label: "Gestor", chipSx: { bgcolor: "#dbeafe", color: "#1d4ed8" } };
  }

  if (role === "leitura") {
    return { label: "Leitura", chipSx: { bgcolor: "#f3f4f6", color: "#4b5563" } };
  }

  return { label: "Usuário", chipSx: { bgcolor: "#fef3c7", color: "#92400e" } };
};

const userFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "action.hover",
    transition: "background-color 150ms ease",
    "&:hover": {
      backgroundColor: "action.selected",
    },
  },
};

const getPasswordValidationMessage = (password) => {
  if (password.length < 8) return "A senha deve ter pelo menos 8 caracteres.";
  if (password.length > 20) return "A senha deve ter no máximo 20 caracteres.";
  if (/\s/.test(password)) return "A senha não pode conter espaços.";
  if (!/\d/.test(password)) return "A senha deve conter pelo menos um número.";
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
    return "A senha deve conter pelo menos um caractere especial.";
  }

  return "";
};

const maskDocumento = (documento) => {
  const value = String(documento || "");
  if (!value) return "";

  const visibleCharacters = 3;
  return `${"*".repeat(Math.max(value.length - visibleCharacters, 0))}${value.slice(-visibleCharacters)}`;
};

const EditarUsuario = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [userSort, setUserSort] = useState({ field: "usuario", direction: "asc" });
  const [openDialog, setOpenDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState(initialUserForm);
  const [produtosDisponiveis, setProdutosDisponiveis] = useState([]);
  const [produtosSelecionados, setProdutosSelecionados] = useState([]);
  const [produtosIniciais, setProdutosIniciais] = useState([]);
  const [loadingPermissoes, setLoadingPermissoes] = useState(false);
  const [savingPermissoes, setSavingPermissoes] = useState(false);
  const [permissionFeedback, setPermissionFeedback] = useState(null);
  const [userDataFeedback, setUserDataFeedback] = useState(null);
  const [showDocumentoCorretor, setShowDocumentoCorretor] = useState(false);

  const { role } = usuariosService.getAuthContext();
  const { contratosAtivos } = useContratosAtivos();
  const currentRole = String(role || "").toLowerCase();
  const isAdmin = getReadonlyFlag() !== true && currentRole !== "user";
  const canViewProductPermissions = true;
  const canEditProductPermissions = isAdmin;
  const isEditing = Boolean(formData.idUsuario);
  const hasAgenteCorretorContract = useMemo(
    () =>
      contratosAtivos.some((contrato) => {
        const idProduto = Number(contrato?.idProduto ?? contrato?.id_produto);
        const nomeProduto = String(contrato?.nomeProduto ?? contrato?.nome_produto ?? "").trim().toLowerCase();

        return idProduto === 1 || nomeProduto === "agente do corretor";
      }),
    [contratosAtivos]
  );
  const isCorretorRole = formData.role === "corretor";
  const produtosParaExibir = useMemo(() => {
    const produtosPorId = new Map();

    produtosDisponiveis.forEach((produto) => {
      const idProduto = Number(produto?.idProduto);
      if (Number.isFinite(idProduto) && !produtosPorId.has(idProduto)) {
        produtosPorId.set(idProduto, produto);
      }
    });

    return Array.from(produtosPorId.values());
  }, [produtosDisponiveis]);

  const produtosAlterados = useMemo(() => {
    const iniciais = new Set(produtosIniciais.map(Number));
    const selecionados = new Set(produtosSelecionados.map(Number));

    return produtosParaExibir
      .map((produto) => Number(produto.idProduto))
      .filter((idProduto) => iniciais.has(idProduto) !== selecionados.has(idProduto));
  }, [produtosIniciais, produtosParaExibir, produtosSelecionados]);

  const loadUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await usuariosService.listUsuarios();
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao carregar usuarios."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsuarios();
  }, [loadUsuarios]);

  useEffect(() => {
    if (!successMessage) return undefined;

    const timer = window.setTimeout(() => setSuccessMessage(""), 2500);
    return () => window.clearTimeout(timer);
  }, [successMessage]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const visibleUsers = usuarios.filter(
      (user) => !["aipim", "setup"].includes(String(user?.role || "").toLowerCase())
    );
    const users = normalizedSearch
      ? visibleUsers.filter((user) => {
          const roleLabel = getRoleChipProps(user?.role).label;
          const statusLabel = user?.ativo ? "ativo" : "inativo";

          return [user?.nome, user?.email, roleLabel, statusLabel].some((value) =>
            String(value || "")
              .toLowerCase()
              .includes(normalizedSearch)
          );
        })
      : visibleUsers;

    const getSortValue = (user) => {
      if (userSort.field === "perfil") return getRoleChipProps(user?.role).label;
      if (userSort.field === "status") return user?.ativo ? 1 : 0;
      if (userSort.field === "criadoEm") return new Date(user?.dataHoraCadastro || 0).getTime() || 0;
      return user?.nome || "";
    };

    return [...users].sort((firstUser, secondUser) => {
      const firstValue = getSortValue(firstUser);
      const secondValue = getSortValue(secondUser);
      const comparison =
        typeof firstValue === "number"
          ? firstValue - secondValue
          : String(firstValue).localeCompare(String(secondValue), "pt-BR", { sensitivity: "base" });

      return userSort.direction === "asc" ? comparison : -comparison;
    });
  }, [usuarios, searchTerm, userSort]);

  const handleUserSort = (field) => {
    setUserSort((current) => ({
      field,
      direction: current.field === field && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleOpenCreateDialog = () => {
    setFormData(initialUserForm);
    setProdutosDisponiveis([]);
    setProdutosSelecionados([]);
    setProdutosIniciais([]);
    setActiveTab(0);
    setOpenDialog(true);
    setError("");
    setPermissionFeedback(null);
    setUserDataFeedback(null);
    setShowDocumentoCorretor(false);
  };

  const handleOpenEditDialog = async (user) => {
    setFormData({
      idUsuario: user.idUsuario,
      nome: user.nome || "",
      email: user.email || "",
      senha: "",
      confirmacaoSenha: "",
      role: user.role || "user",
      ativo: user.ativo !== false,
      primeiroLogin: Boolean(user.primeiroLogin),
      idCorretorExterno: user.idCorretorExterno || "",
      documentoCorretor: user.documentoCorretor || "",
    });
    setOpenDialog(true);
    setActiveTab(canViewProductPermissions && !isAdmin && user.role !== "corretor" ? 1 : 0);
    setError("");
    setPermissionFeedback(null);
    setUserDataFeedback(null);
    setShowDocumentoCorretor(false);

    if (!canViewProductPermissions) return;

    try {
      setLoadingPermissoes(true);
      setProdutosDisponiveis([]);
      setProdutosSelecionados([]);
      setProdutosIniciais([]);

      const [disponiveis, permitidos] = await Promise.all([
        usuariosService.listProdutosPermissao(),
        usuariosService.listProdutosPermitidos(user.idUsuario),
      ]);
      const idsPermitidos = Array.isArray(permitidos)
        ? permitidos.map((produto) => Number(produto?.idProduto)).filter(Number.isFinite)
        : [];

      setProdutosDisponiveis(Array.isArray(disponiveis) ? disponiveis : []);
      setProdutosSelecionados(idsPermitidos);
      setProdutosIniciais(idsPermitidos);
    } catch (err) {
      const message = getApiErrorMessage(err, "Erro ao carregar permissoes de produtos.");
      setPermissionFeedback({ severity: "error", message });
      setError(getApiErrorMessage(err, "Erro ao carregar as permissões de produtos."));
    } finally {
      setLoadingPermissoes(false);
    }
  };

  const handleCloseDialog = () => {
    if (submitting || savingPermissoes) return;
    setOpenDialog(false);
    setFormData(initialUserForm);
    setProdutosDisponiveis([]);
    setProdutosSelecionados([]);
    setProdutosIniciais([]);
    setActiveTab(0);
    setPermissionFeedback(null);
    setUserDataFeedback(null);
    setShowDocumentoCorretor(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
      ...(name === "role" && value !== "corretor"
        ? { idCorretorExterno: "", documentoCorretor: "" }
        : {}),
    }));
  };

  const handleSwitchChange = (event) => {
    const { name, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: checked,
    }));
  };

  const handleSubmit = async () => {
    const showDataValidationError = (message) => {
      setError("");
      setUserDataFeedback({ severity: "error", message });
    };

    setUserDataFeedback(null);

    if (!isAdmin) {
      showDataValidationError("Apenas administradores podem cadastrar ou editar usuarios.");
      return;
    }

    if (!formData.nome.trim()) {
      showDataValidationError("Informe o nome do usuario.");
      return;
    }

    if (!formData.email.trim()) {
      showDataValidationError("Informe o email do usuario.");
      return;
    }

    if (!isEditing && !formData.senha.trim()) {
      showDataValidationError("Informe a senha inicial do usuario.");
      return;
    }

    if (!isEditing && !formData.confirmacaoSenha.trim()) {
      showDataValidationError("Confirme a senha inicial do usuario.");
      return;
    }

    const passwordValidationMessage = formData.senha ? getPasswordValidationMessage(formData.senha) : "";
    if (passwordValidationMessage) {
      showDataValidationError(passwordValidationMessage);
      return;
    }

    if (formData.senha && formData.senha !== formData.confirmacaoSenha) {
      showDataValidationError("Os campos de senha não coincidem.");
      return;
    }

    if (formData.role === "corretor" && !hasAgenteCorretorContract) {
      showDataValidationError("O perfil Corretor esta disponivel apenas para clientes com contrato ativo do produto Agente do Corretor.");
      return;
    }

    if (formData.role === "corretor" && !formData.idCorretorExterno.trim()) {
      showDataValidationError("Informe o ID do corretor no CVCRM.");
      return;
    }

    if (formData.role === "corretor" && !formData.documentoCorretor.trim()) {
      showDataValidationError("Informe o documento/CPF do corretor.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setUserDataFeedback(null);

      const payload = {
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        role: formData.role || "user",
        ativo: formData.ativo,
        primeiroLogin: formData.primeiroLogin,
      };

      if (formData.senha.trim()) {
        payload.senha = formData.senha;
      }

      if (formData.role === "corretor") {
        payload.idCrm = 1;
        payload.idCorretorExterno = formData.idCorretorExterno.trim();
        payload.documentoCorretor = formData.documentoCorretor.trim();
      }

      if (isEditing) {
        await usuariosService.updateUsuario({
          idUsuario: formData.idUsuario,
          ...payload,
        });
        setSuccessMessage("Usuario atualizado com sucesso.");
        setUserDataFeedback({ severity: "success", message: "Dados do usuário atualizados com sucesso." });
      } else {
        await usuariosService.createUsuario(payload);
        setSuccessMessage("Usuario cadastrado com sucesso.");
        setUserDataFeedback({ severity: "success", message: "Usuário cadastrado com sucesso." });
      }

      await loadUsuarios();
    } catch (err) {
      const message = getApiErrorMessage(err, isEditing ? "Erro ao atualizar usuario." : "Erro ao cadastrar usuario.");
      setError(message);
      setUserDataFeedback({ severity: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleProdutoPermissionChange = (idProduto, ativo) => {
    setProdutosSelecionados((current) => {
      const selecionados = new Set(current.map(Number));
      if (ativo) selecionados.add(idProduto);
      else selecionados.delete(idProduto);
      return Array.from(selecionados);
    });
  };

  const handleSaveProductPermissions = async () => {
    if (!canEditProductPermissions) {
      setError("Seu perfil não pode alterar as permissões deste usuário.");
      return;
    }

    if (produtosAlterados.length === 0) return;

    try {
      setSavingPermissoes(true);
      setError("");
      setPermissionFeedback(null);
      const selecionados = new Set(produtosSelecionados.map(Number));
      await usuariosService.updateProdutosPermitidos({
        idUsuario: formData.idUsuario,
        produtos: produtosAlterados.map((idProduto) => ({
          idProduto,
          ativo: selecionados.has(idProduto),
        })),
      });
      setProdutosIniciais([...produtosSelecionados]);
      setSuccessMessage("Permissões de produtos atualizadas com sucesso.");
      setPermissionFeedback({ severity: "success", message: "Permissões de produtos atualizadas com sucesso." });
    } catch (err) {
      const message = getApiErrorMessage(err, "Erro ao atualizar permissoes de produtos.");
      setPermissionFeedback({ severity: "error", message });
      setError(getApiErrorMessage(err, "Erro ao atualizar as permissões de produtos."));
    } finally {
      setSavingPermissoes(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <WelcomeHeader title="Usuarios" subtitle="" />
      </Box>

      {!isAdmin && (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Seu perfil pode visualizar os usuarios do portal. O gerenciamento de cadastro é exclusivo de administradores.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ borderRadius: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: (theme) => `1px solid ${theme.palette.divider}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
          <TextField
            size="small"
            fullWidth
            id="usuarios-search"
            name="usuarios-search-filter"
            type="search"
            autoComplete="new-password"
            placeholder="Buscar por nome, email, perfil ou status"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            inputProps={{ "data-form-type": "other" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <Tooltip title="Limpar busca">
                    <IconButton size="small" edge="end" aria-label="Limpar busca" onClick={() => setSearchTerm("")}>
                      <ClearRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : null,
            }}
            sx={{ maxWidth: { xs: "100%", md: 420 } }}
          />

          <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-end", md: "flex-start" }}>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={handleOpenCreateDialog}
              disabled={!isAdmin}
            >
              Novo usuario
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 4, border: (theme) => `1px solid ${theme.palette.divider}`, overflow: "hidden" }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonOutlineRounded color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Usuarios do portal
              </Typography>
            </Stack>
            <Chip label={`${filteredUsers.length} usuarios`} size="small" variant="outlined" />
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={userSort.field === "usuario" ? userSort.direction : false}>
                    <TableSortLabel
                      active={userSort.field === "usuario"}
                      direction={userSort.field === "usuario" ? userSort.direction : "asc"}
                      onClick={() => handleUserSort("usuario")}
                    >
                      Usuario
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={userSort.field === "perfil" ? userSort.direction : false}>
                    <TableSortLabel
                      active={userSort.field === "perfil"}
                      direction={userSort.field === "perfil" ? userSort.direction : "asc"}
                      onClick={() => handleUserSort("perfil")}
                    >
                      Perfil
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={userSort.field === "status" ? userSort.direction : false}>
                    <TableSortLabel
                      active={userSort.field === "status"}
                      direction={userSort.field === "status" ? userSort.direction : "asc"}
                      onClick={() => handleUserSort("status")}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={userSort.field === "criadoEm" ? userSort.direction : false}>
                    <TableSortLabel
                      active={userSort.field === "criadoEm"}
                      direction={userSort.field === "criadoEm" ? userSort.direction : "asc"}
                      onClick={() => handleUserSort("criadoEm")}
                    >
                      Criado em
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Acao</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 6, textAlign: "center" }}>
                      <Typography color="text.secondary">Nenhum usuario encontrado.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    const roleProps = getRoleChipProps(user.role);

                    return (
                      <TableRow key={user.idUsuario} hover>
                        <TableCell>
                          <Stack spacing={0.35}>
                            <Typography fontWeight={700}>{user.nome}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={roleProps.label}
                            icon={user.role === "admin" ? <AdminPanelSettingsRounded /> : <PersonOutlineRounded />}
                            sx={{
                              textTransform: "capitalize",
                              ...roleProps.chipSx,
                              "& .MuiChip-icon": { color: "currentColor" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={user.ativo ? "Ativo" : "Inativo"}
                            color={user.ativo ? "success" : "default"}
                            variant={user.ativo ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell>{formatDateTime(user.dataHoraCadastro)}</TableCell>
                        <TableCell align="right">
                          <Tooltip
                            title={
                              isAdmin
                                ? "Editar usuario"
                                : canViewProductPermissions
                                  ? "Ver permissões de produtos"
                                  : "Seu perfil não pode gerenciar permissões"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditDialog(user)}
                                disabled={!isAdmin && !canViewProductPermissions}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>{isEditing ? "Editar usuario" : "Novo usuario"}</DialogTitle>
        <DialogContent dividers>
          {isEditing && canViewProductPermissions && formData.role !== "corretor" && (
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="Seções da edição de usuário"
              sx={{ mb: 2, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
            >
              <Tab label="Dados do usuário" value={0} />
              <Tab label="Permissões" value={1} />
            </Tabs>
          )}
          <Stack spacing={2}>
            {activeTab === 0 && (
              <>
            {userDataFeedback && (
              <Alert
                severity={userDataFeedback.severity}
                onClose={isEditing ? () => setUserDataFeedback(null) : undefined}
                sx={{ borderRadius: 2 }}
              >
                {userDataFeedback.message}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, md: 5 }}>
                <TextField
                  label="Nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleFormChange}
                  disabled={!isAdmin}
                  fullWidth
                  required
                  sx={userFieldSx}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 5 }}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  autoComplete="off"
                  value={formData.email}
                  onChange={handleFormChange}
                  disabled={!isAdmin}
                  fullWidth
                  required
                  sx={userFieldSx}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 2 }}>
                <TextField
                  select
                  label="Perfil"
                  name="role"
                  value={formData.role}
                  onChange={handleFormChange}
                  disabled={!isAdmin}
                  fullWidth
                  sx={userFieldSx}
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="gestor">Gestor</MenuItem>
                  <MenuItem value="user">Usuário</MenuItem>
                  <MenuItem value="leitura">Leitura</MenuItem>
                  <MenuItem value="corretor">Corretor</MenuItem>
                </TextField>
              </Grid>
              <Grid item size={{ xs: 12, md: 5 }}>
                <TextField
                  label={isEditing ? "Nova senha" : "Senha"}
                  name="senha"
                  type="password"
                  autoComplete="new-password"
                  value={formData.senha}
                  onChange={handleFormChange}
                  disabled={!isAdmin}
                  fullWidth
                  required={!isEditing}
                  error={Boolean(formData.senha) && Boolean(getPasswordValidationMessage(formData.senha))}
                  helperText={
                    getPasswordValidationMessage(formData.senha) ||
                    "De 8 a 20 caracteres, sem espaços, com número e caractere especial."
                  }
                  sx={userFieldSx}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 5 }}>
                <TextField
                  label="Confirmar senha"
                  name="confirmacaoSenha"
                  type="password"
                  autoComplete="new-password"
                  value={formData.confirmacaoSenha}
                  onChange={handleFormChange}
                  disabled={!isAdmin}
                  fullWidth
                  required={!isEditing || Boolean(formData.senha)}
                  error={Boolean(formData.confirmacaoSenha) && formData.senha !== formData.confirmacaoSenha}
                  helperText={
                    formData.confirmacaoSenha && formData.senha !== formData.confirmacaoSenha
                      ? "As senhas não coincidem."
                      : "Repita a senha informada acima."
                  }
                  sx={userFieldSx}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 2 }}>
                <Box sx={{ height: "100%", display: "flex", alignItems: "flex-start", pt: 0.25 }}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                      width: "100%",
                      minHeight: 56,
                      px: 1.5,
                      py: 0.55,
                      borderRadius: 2.5,
                      bgcolor: "action.hover",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FormControlLabel
                      control={<Switch checked={formData.ativo} onChange={handleSwitchChange} name="ativo" disabled={!isAdmin} />}
                      label="Ativo"
                    />
                  </Paper>
                </Box>
              </Grid>
            </Grid>

            {isCorretorRole && (
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                <Stack spacing={2}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Informe o ID do corretor no CVCRM. O sistema buscará os dados do corretor automaticamente.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="CRM"
                        value="CVCRM"
                        fullWidth
                        disabled
                        helperText="idCrm enviado automaticamente como 1."
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Id do corretor no CVCRM"
                        name="idCorretorExterno"
                        value={formData.idCorretorExterno}
                        onChange={handleFormChange}
                        disabled={!isAdmin}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Documento/CPF"
                        name="documentoCorretor"
                        value={showDocumentoCorretor ? formData.documentoCorretor : maskDocumento(formData.documentoCorretor)}
                        onChange={handleFormChange}
                        onFocus={() => setShowDocumentoCorretor(true)}
                        onBlur={() => setShowDocumentoCorretor(false)}
                        disabled={!isAdmin}
                        fullWidth
                        required
                        InputProps={{
                          endAdornment: formData.documentoCorretor && isAdmin ? (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                size="small"
                                aria-label={showDocumentoCorretor ? "Ocultar CPF" : "Mostrar CPF"}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => setShowDocumentoCorretor((current) => !current)}
                              >
                                {showDocumentoCorretor ? <VisibilityOffRounded fontSize="small" /> : <VisibilityRounded fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>
            )}

              </>
            )}

            {activeTab === 1 && isEditing && canViewProductPermissions && formData.role !== "corretor" && (
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: (theme) => `1px solid ${theme.palette.divider}` }}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography fontWeight={700}>Permissões de produtos</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Selecione os produtos que este usuário poderá acessar.
                    </Typography>
                  </Box>

                  {permissionFeedback && (
                    <Alert
                      severity={permissionFeedback.severity}
                      onClose={() => setPermissionFeedback(null)}
                      sx={{ borderRadius: 2 }}
                    >
                      {permissionFeedback.message}
                    </Alert>
                  )}

                  {currentRole === "gestor" && formData.role !== "user" && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      Gestores podem alterar permissões apenas de usuários com perfil Usuário.
                    </Alert>
                  )}

                  {currentRole === "aipim" && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      Seu perfil possui acesso somente para consulta das permissões.
                    </Alert>
                  )}

                  {loadingPermissoes ? (
                    <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : produtosParaExibir.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum produto disponível para atribuição.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {produtosParaExibir.map((produto) => {
                        const idProduto = Number(produto.idProduto);
                        const selecionado = produtosSelecionados.map(Number).includes(idProduto);

                        return (
                          <Paper key={idProduto} variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 2 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                              <Box>
                                <Typography fontWeight={600}>{produto.nomeProduto}</Typography>
                                {produto.descricaoPlano && (
                                  <Typography variant="body2" color="text.secondary">
                                    {produto.descricaoPlano}
                                  </Typography>
                                )}
                              </Box>
                              <FormControlLabel
                                label={selecionado ? "Permitido" : "Sem permissão"}
                                labelPlacement="start"
                                control={
                                  <Switch
                                    checked={selecionado}
                                    disabled={!canEditProductPermissions || savingPermissoes}
                                    onChange={(event) => handleProdutoPermissionChange(idProduto, event.target.checked)}
                                  />
                                }
                              />
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting || savingPermissoes}>
            Cancelar
          </Button>
          {activeTab === 0 && isAdmin && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting || savingPermissoes || (!isEditing && userDataFeedback?.severity === "success")}
            >
              {submitting ? (isEditing ? "Salvando..." : "Cadastrando...") : isEditing ? "Salvar usuario" : "Cadastrar usuario"}
            </Button>
          )}
          {activeTab === 1 && isEditing && canViewProductPermissions && canEditProductPermissions && (
            <Button
              onClick={handleSaveProductPermissions}
              variant="contained"
              disabled={loadingPermissoes || savingPermissoes || produtosAlterados.length === 0 || submitting}
            >
              {savingPermissoes ? "Salvando permissões..." : "Salvar permissões"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EditarUsuario;
